import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Typography, Box, Button, CircularProgress } from '@mui/material';
// component
// helpers
import { DocumentDownloadLinearIcon, SortLinearIcon } from '../../../icons-lib';
import i18n from '../../../i18';
// custom
import { StyledRoot } from './styles';
import SearchInput from '../../../components/search/Search';
import FilterContainer from '../../../pages/filters/FilterContainer';
import useResponsive from '../../../hooks/useResponsive';
import { fNumber } from '../../../utils/formatNumber';
import { FilterPopover } from '../../../popovers/FilterPopover';
import { HistoryFilter } from '../../../pages/filters/HistoryFilter';

// ----------------------------------------------------------------------

GridListToolbar.propTypes = {
    Head: PropTypes.array,
    numSelected: PropTypes.number,
    handleSearchData: PropTypes.func,
    searchValue: PropTypes.string,
    handleFilterItems: PropTypes.func,
    filters: PropTypes.array,
    exportButton: PropTypes.bool,
    exportDataToCsv: PropTypes.func,
    isCsvExporting: PropTypes.bool,
    deselectAll: PropTypes.func,
    actions: PropTypes.array,
    totalCounter: PropTypes.number,
    showHistoryFilters: PropTypes.bool,
    handleHistoryFilterItems: PropTypes.func,
    historyFilters: PropTypes.array,
    loading: PropTypes.bool
};

export default function GridListToolbar({
    Head,
    numSelected,
    handleSearchData,
    searchValue,
    handleFilterItems,
    filters,
    exportButton,
    exportDataToCsv,
    isCsvExporting,
    deselectAll,
    actions,
    totalCounter,
    showHistoryFilters,
    handleHistoryFilterItems,
    historyFilters,
    historyFieldLabels,
    loading
}) {
    const isMobile = useResponsive('down', 'sm');

    const [filtersAnchor, setFiltersAnchor] = React.useState(null);
    const [historyFilterAnchor, setHistoryFilterAnchor] = React.useState(null);
    const [filterCount, setFilterCount] = React.useState(0);

    const memoizedHead = useMemo(() => Head, [JSON.stringify(Head)]);
    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);
    const memoizedHistoryFilters = useMemo(
        () => historyFilters,
        [JSON.stringify(historyFilters)]
    );

    const handleClearFilters = () => {
        setFilterCount(0);
        handleFilterItems([]);
    };
    const closeFiltersPopover = () => {
        setFiltersAnchor(null);
    };
    const openFiltersPopover = (event) => {
        setFiltersAnchor(event.currentTarget);
    };
    const closeHistoryFiltersPopover = () => {
        setHistoryFilterAnchor(null);
    };
    const handleClearHistoryFilters = () => {
        handleHistoryFilterItems([]);
    };

    const renderFiltersPopover = () => {
        return (
            <FilterPopover
                anchorEl={filtersAnchor}
                setAnchorEl={setFiltersAnchor}
                filterCount={filterCount}
                handleClearFilters={handleClearFilters}
                closeFiltersPopover={closeFiltersPopover}
                title={i18n.t('buttons.filters')}
                filterCountLabel={i18n.t('text.filtersApplied')}
            >
                <FilterContainer
                    setFilterCount={setFilterCount}
                    handleFilterItems={(filterItems) =>
                        handleFilterItems(filterItems)
                    }
                    initFilters={memoizedFilters}
                    Head={Head}
                    loading={loading}
                />
            </FilterPopover>
        );
    };

    const renderHistoryFiltersPopover = () => {
        if (!showHistoryFilters) return null;
        return (
            <FilterPopover
                anchorEl={historyFilterAnchor}
                setAnchorEl={setHistoryFilterAnchor}
                handleClearFilters={handleClearHistoryFilters}
                closeFiltersPopover={closeHistoryFiltersPopover}
                title={i18n.t('buttons.historyFilters')}
                filterLabel={i18n.t('buttons.clear')}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <HistoryFilter
                        initFilters={memoizedHistoryFilters}
                        onFiltersChange={handleHistoryFilterItems}
                        head={memoizedHead}
                        historyFieldLabels={historyFieldLabels}
                        loading={loading}
                    />
                </Box>
            </FilterPopover>
        );
    };

    const isHistoryFilters = historyFilters && historyFilters.length > 0;

    return (
        <StyledRoot
            sx={{
                ...(numSelected > 0 && {
                    color: 'primary.contrastGreyText',
                    backgroundColor: 'background.greyDark'
                })
            }}
        >
            {numSelected > 0 ? (
                <Box display="flex" alignItems="center" gap={1.5}>
                    {!isMobile ? (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => deselectAll()}
                        >
                            {i18n.t('buttons.deselectAll')}
                        </Button>
                    ) : null}
                    <Typography color="text.light">•</Typography>
                    <Typography variant="caption">
                        {numSelected} {i18n.t('text.selected')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box display="flex" alignItems="center" gap={2}>
                        {showHistoryFilters && (
                            <Button
                                variant="outlined"
                                color={isHistoryFilters ? 'info' : 'secondary'}
                                startIcon={<SortLinearIcon />}
                                onClick={(event) =>
                                    setHistoryFilterAnchor(event.currentTarget)
                                }
                            >
                                {i18n.t('buttons.historyFilters')}
                            </Button>
                        )}
                        <Button
                            disabled={isHistoryFilters}
                            variant="outlined"
                            color={
                                filterCount && filters.length
                                    ? 'info'
                                    : 'secondary'
                            }
                            startIcon={<SortLinearIcon />}
                            onClick={openFiltersPopover}
                        >
                            {i18n.t('buttons.filters')}
                        </Button>
                        <SearchInput
                            disabled={isHistoryFilters}
                            searchValue={searchValue}
                            handleSearchData={handleSearchData}
                            sx={{ width: 240 }}
                        />
                        {renderFiltersPopover()}
                        {renderHistoryFiltersPopover()}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography variant="caption" color="text.disabled">
                            {fNumber(totalCounter)} {i18n.t('text.results')}
                        </Typography>
                        {exportButton && (
                            <>
                                <Typography color="text.light">•</Typography>
                                <Button
                                    onClick={exportDataToCsv}
                                    variant="outlined"
                                    color="primary"
                                    disabled={isCsvExporting}
                                    startIcon={
                                        isCsvExporting ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <DocumentDownloadLinearIcon />
                                        )
                                    }
                                >
                                    {isCsvExporting
                                        ? i18n.t('buttons.exporting')
                                        : i18n.t('buttons.exportCsv')}
                                </Button>
                            </>
                        )}
                    </Box>
                </>
            )}

            {numSelected > 0 ? (
                // add start icon
                <Box display="flex" gap={2}>
                    {actions.map((action, index) => (
                        <Button
                            key={index}
                            variant="outlined"
                            color="primary"
                            onClick={action.onClick}
                            startIcon={action.icon}
                        >
                            {action.title}
                        </Button>
                    ))}
                </Box>
            ) : (
                <></>
            )}
        </StyledRoot>
    );
}
